import { C, DEMO_DURATION, along, background, banner, glow, lerp, panel, poly, smooth, text, vessel } from "@/components/demos/canvasKit";

const COAST = [[0.86, 0], [1, 0], [1, 1], [0.9, 1], [0.88, 0.9], [0.91, 0.72], [0.87, 0.58], [0.9, 0.42], [0.87, 0.26], [0.89, 0.1]];
const ROC = [0.945, 0.36];
const PORT = [0.885, 0.82];
const ROUTES = [
  { id: "X-01", pts: [[0.02, 0.22], [0.3, 0.19], [0.55, 0.25], [0.82, 0.21]], speed: 0.028, off: 0.1 },
  { id: "X-02", pts: [[0.82, 0.38], [0.55, 0.42], [0.28, 0.36], [0.02, 0.41]], speed: 0.024, off: 0.55 },
  { id: "X-03", pts: [[0.04, 0.72], [0.28, 0.66], [0.52, 0.72], [0.72, 0.79], [0.87, 0.82]] },
  { id: "X-04", pts: [[0.02, 0.56], [0.3, 0.53], [0.58, 0.58], [0.82, 0.54]], speed: 0.02, off: 0.3 },
  { id: "X-05", pts: [[0.82, 0.94], [0.55, 0.9], [0.3, 0.95], [0.02, 0.91]], speed: 0.026, off: 0.75 },
];
const BANNERS = [
  ["5 SHIPS · ALL AUTONOMOUS · ROC SUPERVISING", "5 SHIPS · ALL AUTONOMOUS", C.cyan],
  ["X-03 · HARBOUR CONTROL CALLING ON VHF", "X-03 · HARBOUR CALLING", C.amber],
  ["ROC TAKES THE CALL THROUGH X-03'S RADIO", "ROC TAKES THE CALL", C.amber],
  ["NEW ARRIVAL TIME AGREED · X-03 SLOWS DOWN", "NEW ETA · X-03 SLOWS", C.cyan],
  ["BACK TO ROUTINE · EXCEPTIONS HANDLED ASHORE", "BACK TO ROUTINE", C.cyan],
];
const STATUS = ["AUTONOMOUS", "VHF CALL", "ROC ON CALL", "NEW ETA 14:20", "AUTONOMOUS"];

const inboundU = (ph) => 0.12 + (0.78 * (Math.min(ph, 0.62) + Math.max(ph - 0.62, 0) * 0.45)) / (0.62 + 0.38 * 0.45);

const drawLink = (ctx, s, roc, t, stage) => {
  const hot = s.flagged;
  ctx.save();
  ctx.globalAlpha = s.alpha * (hot ? 0.95 : 0.28);
  ctx.strokeStyle = hot && stage === 1 ? C.amber : C.cyan;
  ctx.lineWidth = hot ? 1.6 : 1;
  ctx.setLineDash([3, 6]);
  ctx.lineDashOffset = -t * 16;
  ctx.beginPath();
  ctx.moveTo(s.p.x, s.p.y);
  ctx.lineTo(roc.x - 20, roc.y);
  ctx.stroke();
  ctx.restore();
  if (!hot || stage !== 2) return;
  for (let k = 0; k < 2; k++) {
    const f = (t * 0.6 + k * 0.5) % 1;
    const u = k ? 1 - f : f;
    glow(ctx, lerp(s.p.x, roc.x - 20, u), lerp(s.p.y, roc.y, u), 7, C.cyan);
  }
};

const drawPort = (ctx, port, ship, t, calling) => {
  ctx.strokeStyle = C.fg3;
  ctx.strokeRect(port.x - 4, port.y - 4, 8, 8);
  text(ctx, "PORT", port.x + 10, port.y, { color: C.fg3, size: 10.5 });
  if (!calling) return;
  const ang = Math.atan2(ship.y - port.y, ship.x - port.x);
  ctx.save();
  ctx.strokeStyle = C.amber;
  ctx.lineWidth = 1.4;
  for (let k = 0; k < 3; k++) {
    const f = (t * 0.9 + k / 3) % 1;
    ctx.globalAlpha = 1 - f;
    ctx.beginPath();
    ctx.arc(port.x, port.y, 8 + f * 46, ang - 0.5, ang + 0.5);
    ctx.stroke();
  }
  ctx.restore();
};

const drawShip = (ctx, s, narrow, t) => {
  if (s.alpha <= 0.01) return;
  ctx.save();
  ctx.globalAlpha = s.alpha;
  const col = s.flagged ? C.amber : C.cyan;
  vessel(ctx, s.p.x, s.p.y, s.p.ang, narrow ? 13 : 17, col, true);
  if (s.flagged) {
    ctx.strokeStyle = C.amber;
    ctx.beginPath();
    ctx.arc(s.p.x, s.p.y, 15 + Math.sin(t * 6) * 3, 0, Math.PI * 2);
    ctx.stroke();
  }
  if (!narrow || s.id === "X-03") text(ctx, s.id, s.p.x, s.p.y - 17, { color: s.flagged ? C.amber : C.fg2, size: 10.5, align: "center" });
  ctx.restore();
};

const drawRoc = (ctx, roc, t, busy) => {
  glow(ctx, roc.x, roc.y, 34, busy ? "rgba(0,242,254,0.5)" : "rgba(0,242,254,0.28)", 0.75 + 0.25 * Math.sin(t * 2.4));
  ctx.fillStyle = "#0d141d";
  ctx.strokeStyle = C.cyan;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.roundRect(roc.x - 20, roc.y - 13, 40, 26, 13);
  ctx.fill();
  ctx.stroke();
  ctx.lineWidth = 1;
  text(ctx, "ROC", roc.x, roc.y + 0.5, { color: C.cyan, size: 11, align: "center", weight: 600 });
  text(ctx, "ASHORE", roc.x, roc.y + 26, { color: C.fg3, size: 10.5, align: "center" });
  if (busy) text(ctx, "ON CALL", roc.x, roc.y - 24, { color: C.cyan, size: 10.5, align: "center" });
};

const drawPanel = (ctx, h, stage) => {
  const flagCol = stage === 1 || stage === 2 ? C.amber : stage === 3 ? C.cyan : C.fg;
  const rows = ROUTES.map((r) => (r.id === "X-03" ? [r.id, STATUS[stage], flagCol] : [r.id, "AUTONOMOUS", C.fg]));
  const height = rows.length * 17 + 30;
  panel(ctx, 14, (h - height) / 2, rows, { title: "FLEET · NO ONE ONBOARD", width: 196 });
};

export function drawFleet(ctx, w, h, t) {
  background(ctx, w, h);
  const narrow = w < 560;
  const left = narrow ? 0 : 0.25;
  const sx = (x) => (left + (x * (0.86 - left)) / 0.86) * w;
  const ph = (t % DEMO_DURATION) / DEMO_DURATION;
  const stage = ph < 0.25 ? 0 : ph < 0.42 ? 1 : ph < 0.62 ? 2 : ph < 0.8 ? 3 : 4;
  poly(ctx, COAST, w, h);
  const roc = { x: Math.min(ROC[0] * w, w - 26), y: ROC[1] * h };
  const port = { x: PORT[0] * w, y: PORT[1] * h };
  const ships = ROUTES.map((r) => {
    const px = r.pts.map(([x, y]) => [sx(x), y * h]);
    const inbound = r.id === "X-03";
    const u = inbound ? inboundU(ph) : (t * r.speed + r.off) % 1;
    const alpha = inbound ? smooth(0, 0.04, ph) * (1 - smooth(0.96, 1, ph)) : smooth(0, 0.06, u) * (1 - smooth(0.94, 1, u));
    return { id: r.id, p: along(px, u), alpha, flagged: inbound && (stage === 1 || stage === 2) };
  });
  ships.forEach((s) => drawLink(ctx, s, roc, t, stage));
  drawPort(ctx, port, ships[2].p, t, stage === 1);
  ships.forEach((s) => drawShip(ctx, s, narrow, t));
  drawRoc(ctx, roc, t, stage === 2);
  const [long, short, col] = BANNERS[stage];
  banner(ctx, w, 12, narrow ? short : long, col);
  if (!narrow) drawPanel(ctx, h, stage);
}
